/**
 * lazyload.js
 * Fallback for the native `loading="lazy"` attribute used across the theme's
 * <img>/<iframe> tags. Modern browsers lazy load these on their own with no
 * JS required; this only does work in browsers that don't support it yet.
 *
 * Must run un-deferred, as early as possible in <head>, so it can intercept
 * lazy elements via MutationObserver before the browser starts fetching
 * their src/srcset.
 */
(function () {
  'use strict';

  if ('loading' in HTMLImageElement.prototype) {
    return;
  }

  var observer;

  function loadElement(el) {
    if (el.dataset.src) {
      el.src = el.dataset.src;
      delete el.dataset.src;
    }
    if (el.dataset.srcset) {
      el.srcset = el.dataset.srcset;
      delete el.dataset.srcset;
    }
  }

  function observe(el) {
    if (!('IntersectionObserver' in window)) {
      loadElement(el);
      return;
    }
    if (!observer) {
      observer = new IntersectionObserver(function (entries) {
        entries.forEach(function (entry) {
          if (entry.isIntersecting) {
            observer.unobserve(entry.target);
            loadElement(entry.target);
          }
        });
      }, { rootMargin: '100px 0px' });
    }
    observer.observe(el);
  }

  function intercept(el) {
    if (!el || (el.tagName !== 'IMG' && el.tagName !== 'IFRAME')) return;
    if (el.getAttribute('loading') !== 'lazy') return;

    var src = el.getAttribute('src');
    var srcset = el.getAttribute('srcset');

    if (!src && !srcset) {
      // Already stripped - e.g. a slider (slick) clone of an element we
      // processed earlier. It carries the data-src/data-srcset over from
      // the clone but needs its own IntersectionObserver entry.
      if (el.dataset.src || el.dataset.srcset) observe(el);
      return;
    }

    if (src) {
      el.dataset.src = src;
      el.removeAttribute('src');
    }
    if (srcset) {
      el.dataset.srcset = srcset;
      el.removeAttribute('srcset');
    }
    observe(el);
  }

  function interceptTree(root) {
    if (root.querySelectorAll) {
      root.querySelectorAll('img[loading="lazy"], iframe[loading="lazy"]').forEach(intercept);
    }
  }

  interceptTree(document);

  var mutationObserver = new MutationObserver(function (mutations) {
    mutations.forEach(function (mutation) {
      mutation.addedNodes.forEach(function (node) {
        if (node.nodeType !== 1) return;
        intercept(node);
        interceptTree(node);
      });
    });
  });
  mutationObserver.observe(document.documentElement, { childList: true, subtree: true });

  // Left running for the life of the page: sliders (e.g. slick with
  // infinite: true) clone slides - including lazy images - well after
  // DOMContentLoaded, and those clones need to be picked up too.
})();
